"use client";

/**
 * P17A-08 · One client boundary for the nav tree.
 *
 * `Header` (a Server Component) resolves `nav` once and used to pass it down
 * as a prop to two separate client components — `MobileMenuButton` and
 * `PrimaryNav` — each its own server→client boundary, so the tree was
 * serialised into the RSC payload twice for one request. This context makes
 * the crossing happen once: `Header` wraps its nav-consuming markup in
 * `<NavProvider nav={nav}>`, and `MobileMenuButton`/`PrimaryNav` read it back
 * with `useNav()` instead of taking it as a prop.
 */
import { createContext, useContext } from "react";

import type { NavTree } from "~/server/nav/compose-nav-tree";

const NavContext = createContext<NavTree | null>(null);

export function NavProvider({
  nav,
  children,
}: {
  nav: NavTree;
  children: React.ReactNode;
}) {
  return <NavContext.Provider value={nav}>{children}</NavContext.Provider>;
}

/** The active nav tree. Throws outside the provider — every consumer sits under `Header`'s `<NavProvider>`. */
export function useNav(): NavTree {
  const nav = useContext(NavContext);
  if (nav === null) {
    throw new Error("useNav must be used inside <NavProvider>.");
  }
  return nav;
}
